import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import { thresholdAngle, thresholdValue } from './const';
import { Curve2dUtils } from './Curve2dUtils';
import { IPoint2d } from './IPoint2d';
import { Line2d } from './Line2d';
import { LineSeg2d } from './LineSeg2d';
import { Point2dUtils } from './Point2dUtils';
import { IShapePoint2d } from '../types/shape';
import { IPoint3d } from './IPoint3d';
import { calculatePolygonArea } from '../model/polygon/utils';
import { isArc } from '../utils/arc';

interface Edge {
  from: IPoint2d;
  to: IPoint2d;
  angle: number; // 相对于from点的角度(弧度)
}

export interface IIntersectInfo {
  segment: LineSeg2d;
  intersection: IPoint2d;
}

/* 判断两个数差值是否小于阈值 */
export function equalsNumber(number1: number, number2: number) {
  if (Math.abs(number1 - number2) > thresholdValue) {
    return false;
  }

  return true;
}

/* 判断两个多边形是否相等 */
export function equalOutline(outline1: IShapePoint2d[], outline2: IShapePoint2d[]) {
  if (outline1.length !== outline2.length) return false;
  for (let i = 0; i < outline1.length; i++) {
    if (!(equalsNumber(outline1[i].x, outline2[i].x) && equalsNumber(outline1[i].y, outline2[i].y))) return false;
    // TODO: 待完善
  }
  return true;
}

/* 判断两个多边形数组是否相等 */
export function equalOutlines(outlines1: IPoint2d[][], outlines2: IPoint2d[][]) {
  if (outlines1.length !== outlines2.length) return false;
  for (let i = 0; i < outlines1.length; i++) {
    if (!equalOutline(outlines1[i], outlines2[i])) return false;
  }
  return true;
}

export function pointToString(point: IPoint2d): string {
  return `${point.x.toFixed(6)}|${point.y.toFixed(6)}`;
}

export function vectorToString(vector: Vector3): string {
  return `${vector.x.toFixed(6)}-${vector.y.toFixed(6)}-${vector.z.toFixed(6)}`;
}

/* 将角度归一化到[0, 2π)范围 */
export function normalizeAngle(angle: number): number {
  let normalized = angle % (2 * Math.PI);
  if (normalized < 0) normalized += 2 * Math.PI;
  if (Math.PI * 2 - normalized < thresholdValue || normalized < thresholdValue) return 0;
  return normalized;
}

// 辅助函数
export function edgeToString(edge: Edge): string {
  return `${edge.from.x.toFixed(6)},${edge.from.y.toFixed(6)}|${edge.to.x.toFixed(6)},${edge.to.y.toFixed(6)}`;
}

export function pointsToString(start: IPoint2d, end: IPoint2d): string {
  return `${start.x.toFixed(6)},${start.y.toFixed(6)}|${end.x.toFixed(6)},${end.y.toFixed(6)}`;
}

export function angleEqual(a: number, b: number): boolean {
  const absDiff = Math.abs(normalizeAngle(a) - normalizeAngle(b));
  return absDiff < thresholdAngle || absDiff > 2 * Math.PI - thresholdAngle;
}

export function pointEqual(a: IPoint2d, b: IPoint2d): boolean {
  return Math.abs(a.x - b.x) < thresholdValue && Math.abs(a.y - b.y) < thresholdValue;
}

export function vector3dEqual(a: Vector3, b: Vector3) {
  return (
    Math.abs(a.x - b.x) < thresholdValue && Math.abs(a.y - b.y) < thresholdValue && Math.abs(a.z - b.z) < thresholdValue
  );
}

/**
 * 判断2D多边形边界点集是否为顺时针方向，但要保证points形成的路径不能相交
 * @param points 多边形顶点坐标数组，格式为 [x, y][]
 * @returns 如果是顺时针方向返回true，逆时针方向返回false
 */
export function isClockwise(points: IPoint2d[]): boolean {
  if (points.length < 2) {
    return false;
  }

  let sum = 0;
  const len = points.length;
  for (let i = 0; i < len; i++) {
    const current = points[i];
    const next = points[(i + 1) % len];

    // 使用鞋带公式计算
    sum += (next.x - current.x) * (next.y + current.y);
  }

  // 如果sum为正，则是顺时针；为负则是逆时针
  return sum > 0;
}

export function isClockwiseWithArc(points: IShapePoint2d[]): boolean {
  const n = points.length;
  if (n < 2) return false;
  let sum2A = 0;
  for (let i = 0; i < n; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    if (isArc(p1)) {
      const cx = p1.center.x;
      const cy = p1.center.y;
      const r2 = (p1.x - cx) * (p1.x - cx) + (p1.y - cy) * (p1.y - cy);
      const theta1 = Math.atan2(p1.y - cy, p1.x - cx);
      const theta2 = Math.atan2(p2.y - cy, p2.x - cx);
      const dTheta = signedArcDelta(theta1, theta2, p1.clockwise);
      sum2A += r2 * dTheta + cx * (p2.y - p1.y) - cy * (p2.x - p1.x);
    } else {
      sum2A += p1.x * p2.y - p2.x * p1.y;
    }
  }
  // 与原 isClockwise 保持同样的"屏幕坐标系"约定:
  // 屏幕坐标系(y 朝下) -> sum2A < 0 即顺时针
  // 如果你的多边形用数学坐标系(y 朝上) -> sum2A > 0 即顺时针,这里要取反
  return sum2A < 0;
}

// 在 [-2π, 2π] 范围内,根据 clockwise 把 (theta1 -> theta2) 归一化为有向角差
function signedArcDelta(theta1: number, theta2: number, clockwise: boolean): number {
  const TWO_PI = Math.PI * 2;
  if (clockwise) {
    let d = theta1 - theta2;
    while (d < 0) d += TWO_PI;
    while (d > TWO_PI) d -= TWO_PI;
    return -d;
  } else {
    let d = theta2 - theta1;
    while (d < 0) d += TWO_PI;
    while (d > TWO_PI) d -= TWO_PI;
    return d;
  }
}

// 计算多边形几何中心
export function calculateBoundingBoxCenter(points: IPoint2d[]): IPoint2d {
  let minX = points[0].x;
  let maxX = points[0].x;
  let minY = points[0].y;
  let maxY = points[0].y;

  const len = points.length;
  for (let i = 1; i < len; i++) {
    const point = points[i];
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2
  };
}

// 计算多边形的重心
export function calculatePolygonCentroid(points: IPoint2d[]): IPoint2d {
  if (points.length < 3) {
    throw new Error('多边形至少需要3个点');
  }

  let area = 0;
  let centroidX = 0;
  let centroidY = 0;

  // 使用Shoelace公式计算面积和重心
  const len = points.length;
  for (let i = 0; i < len; i++) {
    const j = (i + 1) % len;
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;

    const cross = xi * yj - xj * yi;
    area += cross;
    centroidX += (xi + xj) * cross;
    centroidY += (yi + yj) * cross;
  }

  area /= 2;
  const factor = 1 / (6 * area);

  centroidX *= factor;
  centroidY *= factor;

  return { x: centroidX, y: centroidY };
}

/**
 * 判断两条线段形成的夹角是否为阳角
 * @param prevSegment 前一条线段
 * @param nextSegment 后一条线段
 * @param isPolygonClockwise 多边形顶点是否为顺时针排列（可选）
 * @returns 是否为阳角
 */
export function isConvexAngle(prevSegment: LineSeg2d, nextSegment: LineSeg2d, isPolygonClockwise?: boolean): boolean {
  // 将线段转换为向量
  const vector1 = {
    x: prevSegment.end.x - prevSegment.start.x,
    y: prevSegment.end.y - prevSegment.start.y
  };

  const vector2 = {
    x: nextSegment.end.x - nextSegment.start.x,
    y: nextSegment.end.y - nextSegment.start.y
  };

  // 计算叉积 (vector1 × vector2)
  const crossProduct = vector1.x * vector2.y - vector1.y * vector2.x;

  // 如果没有提供多边形方向，则只根据叉积符号判断
  if (isPolygonClockwise === undefined) {
    return crossProduct > 0;
  }

  // 如果提供了多边形方向，则根据方向判断阳角
  return isPolygonClockwise ? crossProduct < 0 : crossProduct > 0;
}

/**
 * 计算从基点出发的向量相对于参考方向的顺时针角度(0到2π)
 * @param basePoint 基点(主线段端点)
 * @param targetPoint 目标点
 * @param referenceDir 参考方向向量(通常是主线段方向)
 * @returns 顺时针角度(弧度)
 */
export function calculateClockwiseAngle(basePoint: IPoint2d, targetPoint: IPoint2d, referenceDir: IPoint2d): number {
  // 计算目标向量
  const targetDir = {
    x: targetPoint.x - basePoint.x,
    y: targetPoint.y - basePoint.y
  };

  // 计算参考向量的角度(相对于x轴正方向)
  const referenceAngle = Math.atan2(referenceDir.y, referenceDir.x);

  // 计算目标向量的角度(相对于x轴正方向)
  const targetAngle = Math.atan2(targetDir.y, targetDir.x);

  // 计算顺时针角度差(0到2π)
  let angle = referenceAngle - targetAngle;
  if (angle < 0) angle += 2 * Math.PI;

  return angle;
}

/**
 * 对连接线段按顺时针方向排序
 * @param mainLine 主线段
 * @param connectedLines 连接线段数组
 * @param isStart 是否排序起始端连接线段(否则是末端)
 * @returns 排序后的线段数组
 */
export function sortConnectedLinesClockwise(
  mainLine: LineSeg2d,
  connectedLines: LineSeg2d[],
  isStart: boolean = true
): LineSeg2d[] {
  // 确定基点和参考方向
  const basePoint = isStart ? mainLine.start : mainLine.end;
  const referenceDir = isStart
    ? { x: mainLine.end.x - mainLine.start.x, y: mainLine.end.y - mainLine.start.y }
    : { x: mainLine.start.x - mainLine.end.x, y: mainLine.start.y - mainLine.end.y };

  // 创建带角度信息的线段数组
  const linesWithAngles = connectedLines.map((line) => {
    // 确定连接线段的另一个端点
    const otherEnd = isStart
      ? line.start.x === basePoint.x && line.start.y === basePoint.y
        ? line.end
        : line.start
      : line.end.x === basePoint.x && line.end.y === basePoint.y
        ? line.start
        : line.end;

    // 计算顺时针角度
    const angle = calculateClockwiseAngle(basePoint, otherEnd, referenceDir);

    return { line, angle };
  });

  // 按角度从小到大排序(顺时针方向)
  linesWithAngles.sort((a, b) => a.angle - b.angle);

  // 返回排序后的线段
  return linesWithAngles.map((item) => item.line);
}

// 去除重复点
export function removeDuplicatePoints(points: IPoint2d[]): IPoint2d[] {
  const seen = new Map<string, boolean>();
  return points.filter((point) => {
    const key = `${point.x.toFixed(6)},${point.y.toFixed(6)}`;
    if (seen.has(key)) {
      return false;
    }
    seen.set(key, true);
    return true;
  });
}

// 辅助函数：归一化向量
export function normalizeVector(vec: { x: number; y: number }, epsilon: number): { x: number; y: number } {
  const length = Math.hypot(vec.x, vec.y);
  if (length < epsilon) return { x: 0, y: 0 };
  return {
    x: vec.x / length,
    y: vec.y / length
  };
}

/**
 * 在参数空间执行区间减法
 */
export function subtractParamIntervals(
  main: [number, number],
  subtract: [number, number][],
  epsilon: number
): [number, number][] {
  // 合并重叠或相邻的区间
  const merged = mergeParamIntervals(subtract, epsilon);

  let result: [number, number][] = [main];

  for (const interval of merged) {
    const newResult: [number, number][] = [];

    for (const current of result) {
      const diff = subtractParamInterval(current, interval, epsilon);
      newResult.push(...diff);
    }

    result = newResult;
  }

  return result.filter(([start, end]) => start < end - epsilon);
}

/**
 * 合并参数区间
 */
function mergeParamIntervals(intervals: [number, number][], epsilon: number): [number, number][] {
  if (intervals.length === 0) return [];

  // 按起点排序
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);

  const merged: [number, number][] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const current = sorted[i];

    // 检查是否重叠或相邻
    if (current[0] <= last[1] + epsilon) {
      // 合并区间
      last[1] = Math.max(last[1], current[1]);
    } else {
      merged.push(current);
    }
  }

  return merged;
}

/**
 * 单个参数区间减法
 */
function subtractParamInterval(
  main: [number, number],
  subtract: [number, number],
  epsilon: number
): [number, number][] {
  const [mainStart, mainEnd] = main;
  const [subStart, subEnd] = subtract;

  // 无重叠情况
  if (subEnd <= mainStart + epsilon || subStart >= mainEnd - epsilon) {
    return [main];
  }

  // 完全覆盖情况
  if (subStart <= mainStart + epsilon && subEnd >= mainEnd - epsilon) {
    return [];
  }

  // 部分重叠情况
  const result: [number, number][] = [];

  if (mainStart < subStart - epsilon) {
    result.push([mainStart, subStart]);
  }

  if (subEnd < mainEnd - epsilon) {
    result.push([subEnd, mainEnd]);
  }

  return result;
}

// 按距离参考点的距离排序
export function sortPointsByDistance(points: IPoint2d[], reference: IPoint2d): IPoint2d[] {
  return points.sort((a, b) => {
    const distA = Math.sqrt((a.x - reference.x) ** 2 + (a.y - reference.y) ** 2);
    const distB = Math.sqrt((b.x - reference.x) ** 2 + (b.y - reference.y) ** 2);
    return distA - distB; // 按距离升序
  });
}

export function isOrthogonalAngle(angle: number) {
  if (
    !equalsNumber(angle, 0) &&
    !equalsNumber(angle, Math.PI / 2) &&
    !equalsNumber(angle, -Math.PI / 2) &&
    !equalsNumber(angle, Math.PI) &&
    !equalsNumber(angle, -Math.PI) &&
    !equalsNumber(angle, (3 * Math.PI) / 2) &&
    !equalsNumber(angle, 2 * Math.PI)
  ) {
    return false;
  }

  return true;
}

export function calLeftRightIntersection(
  mainLineSeg: LineSeg2d,
  offset: number,
  connectLineSegs: LineSeg2d[],
  thicknesses: number[],
  isStart: boolean
) {
  const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(mainLineSeg, offset);
  const mainLeftLine = new Line2d({
    points: { p1: Point2dUtils.clone(leftCurve.start), p2: Point2dUtils.clone(leftCurve.end) }
  });
  const mainRightLine = new Line2d({
    points: { p1: Point2dUtils.clone(rightCurve.start), p2: Point2dUtils.clone(rightCurve.end) }
  });

  const { left, right } = Curve2dUtils.offsetPoints(
    mainLineSeg,
    isStart ? Point2dUtils.clone(mainLineSeg.start) : Point2dUtils.clone(mainLineSeg.end),
    offset
  );
  let mainLeft: IPoint2d | undefined, mainRight: IPoint2d | undefined;
  if (connectLineSegs.length === 0) {
    mainLeft = left;
    mainRight = right;
  } else {
    const len = connectLineSegs.length;
    // 如果不是首尾相连要重构线段用于计算交点（目的是为了让绕向保持一致，也就是左右偏移线可以对应上）
    const firstConnectLineSeg = isStart
      ? Point2dUtils.equals(mainLineSeg.start, connectLineSegs[0].end)
        ? connectLineSegs[0]
        : new LineSeg2d(connectLineSegs[0].end, connectLineSegs[0].start)
      : Point2dUtils.equals(mainLineSeg.end, connectLineSegs[0].start)
        ? connectLineSegs[0]
        : new LineSeg2d(connectLineSegs[0].end, connectLineSegs[0].start);
    const lastConnectLineSeg = isStart
      ? Point2dUtils.equals(mainLineSeg.start, connectLineSegs[len - 1].end)
        ? connectLineSegs[len - 1]
        : new LineSeg2d(connectLineSegs[len - 1].end, connectLineSegs[len - 1].start)
      : Point2dUtils.equals(mainLineSeg.end, connectLineSegs[len - 1].start)
        ? connectLineSegs[len - 1]
        : new LineSeg2d(connectLineSegs[len - 1].end, connectLineSegs[len - 1].start);

    // 起点的右边让firstLineSeg的又偏移线计算交点
    const firstLineInfo = Curve2dUtils.offsetCurves(firstConnectLineSeg, thicknesses[0] / 2);
    const firstLeftLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(firstLineInfo.leftCurve.start),
        p2: Point2dUtils.clone(firstLineInfo.leftCurve.end)
      }
    });
    const firstRightLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(firstLineInfo.rightCurve.start),
        p2: Point2dUtils.clone(firstLineInfo.rightCurve.end)
      }
    });
    // 起点的右边让lastLineSeg的又偏移线计算交点
    const lastLineInfo = Curve2dUtils.offsetCurves(lastConnectLineSeg, -thicknesses[len - 1] / 2);
    const lastLeftLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(lastLineInfo.leftCurve.start),
        p2: Point2dUtils.clone(lastLineInfo.leftCurve.end)
      }
    });
    const lastRightLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(lastLineInfo.rightCurve.start),
        p2: Point2dUtils.clone(lastLineInfo.rightCurve.end)
      }
    });

    const leftIntersectLine = isStart ? lastLeftLine : firstLeftLine;
    const rightIntersectLine = isStart ? firstRightLine : lastRightLine;

    const leftIntersect = mainLeftLine.isParallelTo(leftIntersectLine)
      ? left
      : mainLeftLine.intersection(leftIntersectLine);
    const rightIntersect = mainRightLine.isParallelTo(rightIntersectLine)
      ? right
      : mainRightLine.intersection(rightIntersectLine);

    mainLeft = leftIntersect ? leftIntersect : left;
    mainRight = rightIntersect ? rightIntersect : right;
  }

  return { mainLeft, mainRight };
}

export function calSegmentIntersectRing(seg: LineSeg2d, shape: IPoint2d[]) {
  const line = new Line2d({
    points: { p1: seg.start, p2: seg.end }
  });

  const intersects: IPoint2d[] = [];
  const len = shape.length;
  for (let i = 0; i < len; i++) {
    const start = shape[i];
    const end = shape[(i + 1) % len];
    const shapeLine = new Line2d({ points: { p1: start, p2: end } });
    if (shapeLine.isParallelTo(line)) {
      continue;
    }

    const shapeSeg = new LineSeg2d(start, end);
    const intersect = line.intersection(shapeLine);
    if (intersect && shapeSeg.pointOnSegment(intersect)) {
      intersects.push(intersect);
    }
  }

  return intersects;
}

/**
 * 计算旋转矩形的四个顶点坐标
 * @param width 矩形宽度
 * @param height 矩形高度
 * @param center 矩形中心
 * @param angleRadians 旋转角度（弧度）
 * @returns 旋转后的四个顶点坐标[左下，右下，右上，左上]
 */
export function getRotatedRectangleVertices(width: number, height: number, center: IPoint2d, angleRadians: number) {
  const centerX = center.x;
  const centerY = center.y;

  // 计算矩形一半的宽度和高度
  const w2 = width / 2;
  const h2 = height / 2;

  // 未旋转矩形的顶点（相对于原点）
  const verticesLocal = [
    [-w2, -h2], // 左下
    [w2, -h2], // 右下
    [w2, h2], // 右上
    [-w2, h2] // 左上
  ];

  // 计算旋转矩阵的参数
  const cosTheta = Math.cos(angleRadians);
  const sinTheta = Math.sin(angleRadians);

  // 旋转并平移顶点
  const verticesGlobal: IPoint2d[] = [];
  for (const vertex of verticesLocal) {
    // 应用旋转
    const x = vertex[0] * cosTheta - vertex[1] * sinTheta;
    const y = vertex[0] * sinTheta + vertex[1] * cosTheta;

    // 平移到中心点
    verticesGlobal.push({ x: x + centerX, y: y + centerY });
  }

  return verticesGlobal;
}

export function getBox3Vertices(params: {
  width: number;
  height: number;
  thickness: number;
  center: IPoint2d;
  rotation: number;
  offGround: number;
}) {
  const { width, height, thickness, center, rotation, offGround } = params;
  const halfWidth = width / 2;
  const halfThickness = thickness / 2;
  const halfHeight = height / 2;

  // 定义旋转矩阵
  const cosTheta = Math.cos(rotation);
  const sinTheta = Math.sin(rotation);

  // 定义相对于中心的8个顶点坐标
  const verticesLocal = [
    [-halfWidth, -halfThickness, -halfHeight], // 左下后
    [halfWidth, -halfThickness, -halfHeight], // 右下后
    [halfWidth, halfThickness, -halfHeight], // 右上后
    [-halfWidth, halfThickness, -halfHeight], // 左上后
    [-halfWidth, -halfThickness, halfHeight], // 左下前
    [halfWidth, -halfThickness, halfHeight], // 右下前
    [halfWidth, halfThickness, halfHeight], // 右上前
    [-halfWidth, halfThickness, halfHeight] // 左上前
  ];

  // 旋转并平移顶点
  const verticesGlobal: IPoint3d[] = [];
  for (const vertex of verticesLocal) {
    // 应用旋转
    const x = vertex[0] * cosTheta - vertex[1] * sinTheta;
    const y = vertex[0] * sinTheta + vertex[1] * cosTheta;
    const z = vertex[2];

    // 平移到中心点
    verticesGlobal.push({ x: x + center.x, y: y + center.y, z: z + offGround });
  }

  return verticesGlobal;
}

export function isCircleIntersectPolygon(center: IPoint2d, radius: number, polygon: IPoint2d[]) {
  let hasIntersect = false;
  for (let i = 0; i < polygon.length; i++) {
    const start = polygon[i];
    const end = polygon[(i + 1) % polygon.length];
    const line = new LineSeg2d(start, end);
    if (line.pointToSegmentDistance(center) < radius) {
      hasIntersect = true;
      break;
    }
  }

  return hasIntersect;
}

export function isCircleInPolygon(center: IPoint2d, radius: number, polygon: IPoint2d[]) {
  const isCenterOnPolygon = Curve2dUtils.isPointInPolygon(center, polygon);
  if (!isCenterOnPolygon) return false;
  const result = !isCircleIntersectPolygon(center, radius, polygon);

  return result;
}

export function getNearestPoint(points: IPoint2d[], point: IPoint2d): IPoint2d | null {
  if (points.length === 0) return null;
  if (points.length === 1) return points[0];

  let minDistance = Infinity;
  let nearestPoint: IPoint2d | null = null;
  for (const p of points) {
    const distance = Point2dUtils.distanceTo(p, point);
    if (distance < minDistance) {
      minDistance = distance;
      nearestPoint = p;
    }
  }
  return nearestPoint;
}

// startAngle 到 endAngle 所扫过的角度
export function getSweepAngle(startDirection: IPoint2d, endDirection: IPoint2d, clockwise: boolean): number {
  const startAngle = Math.atan2(startDirection.y, startDirection.x);
  const endAngle = Math.atan2(endDirection.y, endDirection.x);
  return clockwise ? normalizeAngle(startAngle - endAngle) : normalizeAngle(endAngle - startAngle);
}

export function getMatrix(position: Vector3, rotation: number) {
  const euler = new Euler(0, 0, rotation); // XYZ顺序
  const matrix = new Matrix4();
  matrix.makeRotationFromEuler(euler);
  matrix.setPosition(position);

  return matrix;
}

export function calculateMatrix(from: Vector3, to: Vector3, startRotation: number, endRotation: number): Matrix4 {
  // 直接计算变换矩阵（从起点到终点）
  const transformMatrix = new Matrix4();

  // 计算位移增量
  const deltaPos = new Vector3().subVectors(to, from);

  const startRot = new Euler(0, 0, startRotation);
  const endRot = new Euler(0, 0, endRotation);
  // 计算旋转增量
  const startQuat = new Quaternion().setFromEuler(startRot);
  const endQuat = new Quaternion().setFromEuler(endRot);
  const deltaQuat = startQuat.clone().conjugate().multiply(endQuat);

  // 构建变换矩阵
  transformMatrix.compose(deltaPos, deltaQuat, new Vector3(1, 1, 1));

  return transformMatrix;
}

export function relativeToWorldInfo(relativeMatrix: Matrix4, worldMatrix: Matrix4) {
  // 家具和模型间的关联矩阵
  const furnitureMoveMatrix = worldMatrix.clone().multiply(relativeMatrix);

  const position = new Vector3();
  const quaternion = new Quaternion();
  const scale = new Vector3();
  furnitureMoveMatrix.decompose(position, quaternion, scale);
  // 转换为欧拉角
  const rotation = new Euler().setFromQuaternion(quaternion);

  return { position, rotation };
}

export interface IPlacement2d {
  center: IPoint2d;
  rotation: number;
}

/**
 * 创建坐标旋转映射，将对象的中心点和旋转角从原参考系映射到目标参考系。
 * 返回的旋转角已归一化到 [0, 2π)：矩阵分解出的欧拉角落在 (-π, π]，直接写入数据会让 200° 变成 -160°。
 */
export function createPlacementMapper(
  originFrame: IPlacement2d,
  targetFrame: IPlacement2d
): (placement: IPlacement2d) => IPlacement2d {
  const originMatrixInverted = calculateMatrix(
    new Vector3(0, 0, 0),
    new Vector3(originFrame.center.x, originFrame.center.y, 0),
    0,
    originFrame.rotation
  ).invert();

  const targetMatrix = calculateMatrix(
    new Vector3(0, 0, 0),
    new Vector3(targetFrame.center.x, targetFrame.center.y, 0),
    0,
    targetFrame.rotation
  );

  return (placement: IPlacement2d) => {
    const poseMatrix = calculateMatrix(
      new Vector3(0, 0, 0),
      new Vector3(placement.center.x, placement.center.y, 0),
      0,
      placement.rotation
    );

    // 计算对象相对于原参考系的变换，并映射到目标参考系
    const relativeMatrix = originMatrixInverted.clone().multiply(poseMatrix);
    const { position, rotation } = relativeToWorldInfo(relativeMatrix, targetMatrix);

    return { center: { x: position.x, y: position.y }, rotation: normalizeAngle(rotation.z) };
  };
}

/**
 * 判断已顺时针/逆时针排序的点集能否形成一个简单的多边形
 * @param points 已排序的点集
 * @param limitArea 可选，是否限制面积
 */
export function isSimplePolygon<T extends IPoint2d>(points: T[], limitArea?: number): boolean {
  if (points.length < 3) return false;

  const pointLength = points.length;
  for (let i = 0; i < pointLength; i++) {
    const a1 = points[i];
    const a2 = points[(i + 1) % pointLength];

    for (let j = i + 1; j < pointLength; j++) {
      if (edgesAreAdjacent(i, j, pointLength)) continue;

      const b1 = points[j];
      const b2 = points[(j + 1) % pointLength];

      if (new LineSeg2d(a1, a2).intersection(new LineSeg2d(b1, b2))) {
        return false;
      }
    }
  }

  if (limitArea) {
    const area = calculatePolygonArea(points);
    if (area <= limitArea) return false;
  }

  return true;
}

export function edgesAreAdjacent(i: number, j: number, total: number): boolean {
  if (i === j) return true;
  const iNext = (i + 1) % total;
  const jNext = (j + 1) % total;
  return iNext === j || jNext === i;
}
